import React, { useState, useEffect } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import DonorWall from "../components/DonorWall";
import { ShareButtons } from "../components/ShareButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────
// LINKS — swap these placeholders when you create your Stripe payment links
// ─────────────────────────────────────────────────────────────────────────────
const MONTHLY_STRIPE_URL  = process.env.REACT_APP_STRIPE_MONTHLY;
const ONE_TIME_URL        = process.env.REACT_APP_STRIPE_ONE_TIME;
const ANY_AMOUNT_URL      = process.env.REACT_APP_STRIPE_ONE_TIME;
const TITLE_CONTACT       = "mailto:info@seeleyskatepark.org";
const SPONSOR_LINKS = {
    "pyramid-peak":      ANY_AMOUNT_URL,
    "crescent-peak":     ANY_AMOUNT_URL,
    "devine-peak":       ANY_AMOUNT_URL,
    "marshall-mountain": ANY_AMOUNT_URL,
    "rice-ridge":        ANY_AMOUNT_URL,
    "big-gus":           ANY_AMOUNT_URL,
};

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────
const PACKAGES = [
    { id: "supporter",  amount: 10,  label: "Supporter",       url: process.env.REACT_APP_STRIPE_10 },
    { id: "fan",        amount: 25,  label: "Fan",              url: process.env.REACT_APP_STRIPE_25 },
    { id: "backer",     amount: 50,  label: "Backer",           url: process.env.REACT_APP_STRIPE_50 },
    { id: "champion",   amount: 100, label: "Champion",         url: process.env.REACT_APP_STRIPE_100 },
    { id: "patron",     amount: 250, label: "Patron",           url: process.env.REACT_APP_STRIPE_250 },
    { id: "founding",   amount: 500, label: "Founding Member",  url: process.env.REACT_APP_STRIPE_500 },
];

const TIERS = [
    {
        id: "pyramid-peak",
        name: "Pyramid Peak Partner",
        amount: "$25,000+",
        level: "major",
        benefits: [
            "Name / logo on park signage",
            "Name / logo on website",
            "Social media recognition",
            "Recognition at fundraising events",
        ],
    },
    {
        id: "crescent-peak",
        name: "Crescent Peak Partner",
        amount: "$15,000–$24,999",
        level: "major",
        benefits: [
            "Name / logo on park signage",
            "Name / logo on website",
            "Social media recognition",
        ],
    },
    {
        id: "devine-peak",
        name: "Devine Peak Partner",
        amount: "$5,000–$14,999",
        level: "major",
        benefits: [
            "Name / logo on park signage",
            "Name / logo on website",
        ],
    },
    {
        id: "marshall-mountain",
        name: "Marshall Mountain Partner",
        amount: "$2,500–$4,999",
        level: "community",
        benefits: ["Name on park signage", "Name on website"],
    },
    {
        id: "rice-ridge",
        name: "Rice Ridge Partner",
        amount: "$500–$2,499",
        level: "community",
        benefits: ["Name on website"],
    },
    {
        id: "big-gus",
        name: "Big Gus Partner",
        amount: "Up to $499",
        level: "community",
        benefits: ["Thank-you recognition on website"],
    },
];
const MAJOR_TIERS = TIERS.filter(t => t.level === "major");
const COMMUNITY_TIERS = TIERS.filter(t => t.level === "community");

const FAQ_ITEMS = [
    {
        q: "Is my donation tax-deductible?",
        a: "Yes. The Seeley Lake Skate Park Association is a registered 501(c)(3) nonprofit. Your contribution is tax-deductible to the extent allowed by law. You'll receive a receipt from Stripe for your records.",
    },
    {
        q: "How will funds be used?",
        a: "All funds go directly toward designing, permitting, and building a concrete skate park in Seeley Lake — including site prep, materials, construction, and long-term maintenance. We post regular progress updates here and on social media.",
    },
    {
        q: "Can businesses sponsor the park?",
        a: "Yes, and we'd love to partner with local businesses. Sponsorship starts at $500 (Rice Ridge Partner) and goes all the way up to naming-rights level (The Bob Partner). Each tier includes meaningful, lasting recognition in the park and in the community.",
    },
    {
        q: "Can I make a recurring monthly donation?",
        a: "Yes. Our $25/month sustaining supporter option is one of the most powerful ways to contribute — reliable monthly income helps us plan and execute the build. You can cancel anytime, no questions asked.",
    },
];

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const BenefitRow = ({ text }) => (
    <li className="flex items-start gap-2.5 text-sm font-manrope text-[#3D2008]">
        <Check size={14} className="text-[#1B5E3B] mt-0.5 flex-shrink-0 stroke-[2.5]" />
        <span>{text}</span>
    </li>
);

const SponsorCard = ({ tier }) => {
    const href = SPONSOR_LINKS[tier.id];
    const isMajor = tier.level === "major";
    return (
        <div
            className="bg-white border border-[#D5D0C7] hover:border-[#1B5E3B] hover:shadow-[0_4px_16px_rgba(27,94,59,0.12)] transition-all duration-200 p-6 flex flex-col"
            data-testid={`tier-${tier.id}`}
        >
            <span className={`font-manrope text-[11px] font-bold uppercase tracking-[0.12em] mb-2 ${isMajor ? "text-[#1B5E3B]" : "text-[#8A7A65]"}`}>
                {isMajor ? "Major Partner" : "Community Partner"}
            </span>
            <h3 className="font-anton text-xl text-[#3D2008] uppercase leading-tight mb-1">{tier.name}</h3>
            <p className="font-manrope font-bold text-2xl text-[#3D2008] mb-5">{tier.amount}</p>
            <ul className="space-y-2 flex-1 mb-6">
                {tier.benefits.map(b => <BenefitRow key={b} text={b} />)}
            </ul>
            <a
                href={href}
                target={(href || "").startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={`tier-cta-${tier.id}`}
                className="block text-center py-2.5 px-4 border-2 border-[#1B5E3B] text-[#1B5E3B] font-manrope font-semibold text-sm hover:bg-[#1B5E3B] hover:text-white transition-colors duration-200"
            >
                Become a Sponsor
            </a>
        </div>
    );
};

const FaqItem = ({ item }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-[#D5D0C7]">
            <button
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                className="w-full flex justify-between items-center gap-4 py-5 text-left"
            >
                <span className="font-manrope font-semibold text-[#3D2008] text-base leading-snug">{item.q}</span>
                {open
                    ? <ChevronUp size={17} className="text-[#1B5E3B] flex-shrink-0" />
                    : <ChevronDown size={17} className="text-[#8A7A65] flex-shrink-0" />
                }
            </button>
            {open && (
                <p className="pb-5 font-manrope text-sm text-[#5C4A35] leading-relaxed">{item.a}</p>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const Donate = () => {
    const [stats, setStats] = useState({ total_raised: 0, donor_count: 0, goal: 400000, percentage: 0 });

    useEffect(() => {
        axios.get(`${API}/donate/stats`).then(r => setStats(r.data)).catch(() => {});
    }, []);

    const pkg = PACKAGES.find(p => p.id === "backer"); // default display only

    return (
        <div className="pt-16">

            {/* ═══════════════════════════════════════════════════════════════
                1. PAGE HERO
            ═══════════════════════════════════════════════════════════════ */}
            <section className="py-20 px-4 bg-[#3D2008]" data-testid="donate-hero">
                <div className="max-w-3xl mx-auto">
                    <p className="font-manrope text-[#C9A84C] text-xs font-bold uppercase tracking-[0.15em] mb-5">Seeley Lake Skate Park Association</p>
                    <h1 className="font-anton text-5xl sm:text-7xl text-[#F5EDD6] uppercase leading-none mb-5">
                        Help Us Build It
                    </h1>
                    <p className="font-manrope text-[#F5EDD6]/75 text-lg leading-relaxed max-w-2xl">
                        This is a community project — funded by the people who live here, raise families here, and want to give young riders a real place to skate. Every dollar moves us closer to breaking ground.
                    </p>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════════
                2. FUNDRAISING PROGRESS
            ═══════════════════════════════════════════════════════════════ */}
            <section className="py-8 px-4 bg-[#1B5E3B]" data-testid="donate-progress-section">
                <div className="max-w-3xl mx-auto">
                    <div className="flex justify-between items-end mb-2">
                        <div>
                            <span data-testid="donate-raised-amount" className="font-anton text-3xl text-white">
                                ${stats.total_raised.toLocaleString()}
                            </span>
                            <span className="font-manrope text-white/55 text-sm ml-2">
                                raised of ${stats.goal.toLocaleString()} goal
                            </span>
                        </div>
                        <span data-testid="donate-percentage" className="font-manrope text-xs text-white/55">
                            {stats.donor_count} {stats.donor_count === 1 ? "donor" : "donors"} · {stats.percentage.toFixed(1)}%
                        </span>
                    </div>
                    <div className="h-2.5 bg-white/20 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-white rounded-full transition-all duration-700 ease-out"
                            style={{ width: `${Math.max(stats.percentage, 0.5)}%` }}
                        />
                    </div>
                    <p className="font-manrope text-white/40 text-xs mt-2">Goal: $400,000 toward park design and construction</p>
                </div>
            </section>


            {/* ═══════════════════════════════════════════════════════════════
                4. DONATION OPTIONS — Monthly + One-time cards
            ═══════════════════════════════════════════════════════════════ */}
            <section className="py-16 px-4 bg-[#F5EDD6]" id="give" data-testid="donation-options-section">
                <div className="max-w-3xl mx-auto">
                    <h2 className="font-anton text-3xl text-[#3D2008] uppercase mb-1">Ways to Give</h2>
                    <p className="font-manrope text-[#5C4A35] text-sm mb-10 max-w-xl">
                        Choose whatever works for you. Both options go directly toward building the park.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                        {/* Monthly recurring — featured */}
                        <div className="bg-[#1B5E3B] p-7 flex flex-col" data-testid="monthly-donation-card">
                            <span className="font-manrope text-[11px] font-bold uppercase tracking-[0.14em] text-[#8FC9A6] mb-4">
                                Sustaining Supporter
                            </span>
                            <h3 className="font-anton text-2xl text-white uppercase leading-tight mb-1">
                                Monthly Donation
                            </h3>
                            <div className="flex items-baseline gap-1 mb-4">
                                <span className="font-anton text-4xl text-white">$25</span>
                                <span className="font-manrope text-sm text-white/55">/month</span>
                            </div>
                            <p className="font-manrope text-white/75 text-sm leading-relaxed flex-1 mb-6">
                                Become a sustaining supporter of the Seeley Lake Skate Park with a recurring monthly contribution. Reliable monthly support is the most powerful way to help us plan and build. Cancel anytime.
                            </p>
                            <a
                                href={MONTHLY_STRIPE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="monthly-donate-btn"
                                className="block text-center py-3 px-5 bg-white text-[#1B5E3B] font-manrope font-bold text-sm hover:bg-[#F5EDD6] transition-colors duration-150"
                            >
                                Give $25 / month
                            </a>
                        </div>

                        {/* One-time */}
                        <div className="bg-white border border-[#D5D0C7] p-7 flex flex-col" data-testid="onetime-donation-card">
                            <span className="font-manrope text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A7A65] mb-4">
                                One-Time Gift
                            </span>
                            <h3 className="font-anton text-2xl text-[#3D2008] uppercase leading-tight mb-1">
                                Single Donation
                            </h3>
                            <div className="flex items-baseline gap-1 mb-4">
                                <span className="font-anton text-4xl text-[#3D2008]">Any Amount</span>
                            </div>
                            <p className="font-manrope text-[#5C4A35] text-sm leading-relaxed flex-1 mb-6">
                                Give once in any amount. Ten dollars or ten thousand — every gift moves this project forward. You can also select a specific amount below.
                            </p>
                            <a
                                href={ONE_TIME_URL}
                                target={(ONE_TIME_URL || "").startsWith("http") ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                data-testid="onetime-donate-btn"
                                className="block text-center py-3 px-5 border-2 border-[#3D2008] text-[#3D2008] font-manrope font-bold text-sm hover:bg-[#3D2008] hover:text-white transition-colors duration-150"
                            >
                                Make a One-Time Gift
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════════
                5. CHOOSE YOUR AMOUNT — direct Stripe links, no backend
            ═══════════════════════════════════════════════════════════════ */}
            <section className="py-16 px-4 bg-white border-t border-[#D5D0C7]" data-testid="donation-form-section">
                <div className="max-w-3xl mx-auto">
                    <h2 className="font-anton text-3xl text-[#3D2008] uppercase mb-1">Choose Your Amount</h2>
                    <p className="font-manrope text-[#5C4A35] text-sm mb-8 max-w-xl">
                        Select a suggested amount — you can adjust it on the next page before paying.
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                        {PACKAGES.map(p => (
                            <a
                                key={p.id}
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`pkg-${p.id}`}
                                className="p-4 border-2 border-[#D5D0C7] bg-white text-left hover:border-[#1B5E3B] hover:shadow-md transition-all duration-150 block group"
                            >
                                <div className="font-anton text-2xl mb-0.5 text-[#1B5E3B]">${p.amount}</div>
                                <div className="font-manrope text-xs font-semibold uppercase tracking-wide text-[#8A7A65] group-hover:text-[#1B5E3B] transition-colors">{p.label}</div>
                            </a>
                        ))}
                    </div>

                    <a
                        href={ONE_TIME_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="donate-submit-btn"
                        className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-3.5 bg-[#1B5E3B] text-white font-manrope font-bold text-sm hover:bg-[#155030] transition-colors duration-200"
                    >
                        Donate Now — Any Amount
                    </a>
                    <p className="font-manrope text-xs text-[#B0A898] mt-3">
                        Processed securely via Stripe. SLSA is a registered 501(c)(3) nonprofit.
                    </p>

                    <div className="mt-8 pt-6 border-t border-[#D5D0C7]" data-testid="donate-share-section">
                        <ShareButtons
                            testId="donate-share"
                            label="Can't give today? Share it"
                            url={`${window.location.origin}/donate`}
                            title="Help build the Seeley Lake Skatepark"
                            text="Help build a free concrete skatepark for the kids of Seeley Lake, MT! Every dollar counts:"
                        />
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════════
                6. SPONSORSHIP SECTION
            ═══════════════════════════════════════════════════════════════ */}
            <section
                className="py-20 px-4 bg-[#F5EDD6] border-t border-[#D5D0C7]"
                id="sponsor"
                data-testid="sponsorship-section"
            >
                <div className="max-w-5xl mx-auto">
                    <p className="font-manrope text-[#1B5E3B] text-[11px] font-bold uppercase tracking-[0.15em] mb-5">
                        Sponsorship
                    </p>
                    <h2 className="font-anton text-4xl sm:text-5xl text-[#3D2008] uppercase mb-4">
                        Partner With Us
                    </h2>
                    <p className="font-manrope text-[#5C4A35] text-base max-w-2xl leading-relaxed mb-14">
                        Sponsorship tiers are named after the peaks and ridges that surround Seeley Lake — a permanent part of the landscape, just like your support will be. Partners are recognized on park signage, on our website, and at every SLSA event.
                    </p>

                    {/* THE BOB — Title Partner (featured) */}
                    <div
                        className="bg-[#3D2008] border border-[#C9A84C] p-8 sm:p-10 mb-10"
                        data-testid="title-tier-card"
                    >
                        <div className="flex flex-col lg:flex-row lg:items-start gap-8">
                            <div className="flex-1">
                                <span className="inline-block bg-[#C9A84C] text-[#3D2008] font-manrope text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1 mb-5">
                                    Title Partner
                                </span>
                                <h3 className="font-anton text-3xl sm:text-4xl text-[#F5EDD6] uppercase mb-3">
                                    The Bob Partner
                                </h3>
                                <p className="font-manrope text-[#F5EDD6]/75 text-base leading-relaxed max-w-xl mb-5">
                                    Named for the Bob Marshall Wilderness — the wild country that defines this corner of Montana. As The Bob Partner, your name becomes woven into the park's identity: on the entrance sign, in every official mention, and in this community for generations. This is a naming-rights partnership that requires a direct conversation.
                                </p>
                                <ul className="space-y-2">
                                    {[
                                        "Permanent naming recognition on park entrance signage",
                                        "Top billing on all SLSA materials, website, and social media",
                                        "Featured at all fundraising events and the grand opening",
                                        "Enduring legacy in the Seeley Lake community",
                                    ].map(b => (
                                        <li key={b} className="flex items-start gap-2.5 font-manrope text-sm text-[#F5EDD6]/65">
                                            <Check size={13} className="text-[#C9A84C] mt-0.5 flex-shrink-0 stroke-[2.5]" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="lg:flex-shrink-0 lg:pt-1">
                                <a
                                    href={TITLE_CONTACT}
                                    data-testid="title-sponsor-cta"
                                    className="inline-block whitespace-nowrap py-3 px-6 border-2 border-[#C9A84C] text-[#C9A84C] font-manrope font-semibold text-sm hover:bg-[#C9A84C] hover:text-[#3D2008] transition-colors duration-200"
                                >
                                    Contact Us to Become a Title Sponsor
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Major Partners */}
                    <div className="mb-10">
                        <p className="font-manrope text-[11px] font-bold uppercase tracking-[0.14em] text-[#1B5E3B] mb-4">
                            Major Partners
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {MAJOR_TIERS.map(t => <SponsorCard key={t.id} tier={t} />)}
                        </div>
                    </div>

                    {/* Community Partners */}
                    <div>
                        <p className="font-manrope text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A7A65] mb-4">
                            Community Partners
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {COMMUNITY_TIERS.map(t => <SponsorCard key={t.id} tier={t} />)}
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════════
                7. CONTACT CTA — major donors / local businesses
            ═══════════════════════════════════════════════════════════════ */}
            <section className="py-14 px-4 bg-[#1B5E3B]" data-testid="contact-cta-section">
                <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-8 items-center">
                    <div>
                        <h2 className="font-anton text-3xl text-white uppercase mb-3">Questions About Giving?</h2>
                        <p className="font-manrope text-white/75 text-base leading-relaxed">
                            For major gifts, matching donations, in-kind contributions, or anything that doesn't fit a standard tier — we'd rather have a real conversation than put you through a form.
                        </p>
                    </div>
                    <div className="flex flex-col items-start gap-3">
                        <a
                            href="/contact"
                            data-testid="contact-major-donor-btn"
                            className="inline-block py-3 px-7 bg-white text-[#1B5E3B] font-manrope font-bold text-sm hover:bg-[#F5EDD6] transition-colors duration-150"
                        >
                            Get in Touch
                        </a>
                        <p className="font-manrope text-white/40 text-xs">We typically respond within 48 hours.</p>
                    </div>
                </div>
            </section>

            <DonorWall />

            {/* ═══════════════════════════════════════════════════════════════
                8. FAQ
            ═══════════════════════════════════════════════════════════════ */}
            <section className="py-16 px-4 bg-white border-t border-[#D5D0C7]" data-testid="faq-section">
                <div className="max-w-3xl mx-auto">
                    <h2 className="font-anton text-3xl text-[#3D2008] uppercase mb-8">Common Questions</h2>
                    <div>
                        {FAQ_ITEMS.map(item => <FaqItem key={item.q} item={item} />)}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default Donate;
